import { Channel } from '../../../../interfaces';
import { getAuth } from '../../../../utils/auth-utils';
import { mapThruthy } from '../../../../utils/commonUtils';
import { getChannelLogoUrl } from '../../../../utils/tvUtils';
import { getImageByPath } from '../../common';
import type { Title } from '../../titles/types/title';
import { Bookmark } from '../bookmarks/types';
import type { SubscribedProduct } from '../products/types';
import type { Provider, SvodProvider, ThirdPartyProvider } from './types';

function hasOrderProvidersBy(
  title: Title,
): title is Title & { orderProvidersBy: NonNullable<Title['orderProvidersBy']> } {
  return title.orderProvidersBy !== null && title.orderProvidersBy !== undefined;
}

interface SelectProviderOptions {
  title: Title;
  providers: Provider[];
  products: SubscribedProduct[];
  bookmark: Bookmark | undefined;
}

export function selectProvider(options: SelectProviderOptions): Provider | undefined {
  const providers = mapThruthy(options.title.providers, (id) => {
    return options.providers.find((p) => p.id === id);
  });

  const products = [...options.products.values()];

  if (hasOrderProvidersBy(options.title) && options.title.orderProvidersBy.id !== null) {
    const providerById = findById({
      providers: providers,
      id: options.title.orderProvidersBy.id,
    });

    if (providerById !== undefined) {
      return providerById;
    }
  }

  if (options.bookmark !== undefined) {
    const providerByBookmark = findById({
      providers: providers,
      id: options.bookmark.providerIdentifier,
    });

    if (providerByBookmark !== undefined) {
      return providerByBookmark;
    }
  }

  if (options.title.bookmark !== undefined) {
    const providerByBookmark = findById({
      providers: providers,
      id: options.title.bookmark.providerIdentifier,
    });

    if (providerByBookmark !== undefined) {
      return providerByBookmark;
    }
  }

  if (hasOrderProvidersBy(options.title) && options.title.orderProvidersBy.type !== null) {
    const providerByType = findByType({
      providers: providers,
      masterId: options.title.masterId,
      type: options.title.orderProvidersBy.type,
      products: products,
    });

    if (providerByType !== undefined) {
      return providerByType;
    }
  }

  return sortProvidersByPlayability(providers, products, options.title.masterId)[0];
}

function sortProvidersByPlayability(providers: Provider[], products: SubscribedProduct[], masterId: string) {
  return [...providers].sort((a, b) => {
    const isAPlayable = isProviderPlayable(a, { products, masterId });
    const isBPlayable = isProviderPlayable(b, { products, masterId });
    return Number(isAPlayable) - Number(isBPlayable);
  });
}

interface FindByIdOptions {
  providers: Provider[];
  id: string;
}

function findById({ providers, id }: FindByIdOptions) {
  return providers.find((x) => x.id.toLowerCase() === id.toLowerCase());
}

interface FindByTypeOptions {
  providers: Provider[];
  products: SubscribedProduct[];
  masterId: string;
  type: Lowercase<Provider['type']>;
}

function findByType({ providers, products, masterId, type }: FindByTypeOptions) {
  const providersByType = providers.filter((x) => x.type.toLowerCase() === type.toLowerCase());

  if (providersByType.length === 0) {
    return undefined;
  }

  return sortProvidersByPlayability(providers, products, masterId)[0];
}
interface IsVodProviderPlayableOptions {
  products: SubscribedProduct[];
  masterId: string;
}

function isProviderPlayable(provider: Provider, options: IsVodProviderPlayableOptions): boolean {
  if (provider.type === 'vod') {
    if (options === undefined) {
      throw new Error('Products and masterId are required to check if VOD provider is playable');
    }

    const product = options.products.find((x) => x.masterId === options.masterId);
    return product !== undefined;
  }

  return provider.isSubscribed;
}

export function createProviders(items: Provider[] | undefined, channels: Channel[]) {
  const atsProviders = prepareAtsProviders(items);

  if (!channels || channels.length === 0) {
    return atsProviders;
  }

  const channelProviders = createProgramProviders(channels, atsProviders);

  // ATS providers first because more often than not,
  // they will be the providers we are looking for.
  return [...atsProviders, ...channelProviders];
}

export function prepareAtsProviders(providersList: Provider[] | undefined) {
  if (!providersList) {
    return [];
  }

  return providersList.map((item) => {
    if (item.type !== 'program') {
      item.image = getImageByPath(item.image, 'square', 150);
    }

    return item;
  });
}

function createProgramProviders(channels: Channel[], providers: Provider[]): Provider[] {
  const auth = getAuth();

  return channels.map((channel) => {
    const isSubscribed = !channel.channelPermissions.notInSubscription;
    const supportsLiveTV = channel.channelPermissions.onlyInsideforLiveTV ? auth.isHome : isSubscribed;
    const isLimitedToHomeNetwork = channel.channelPermissions.onlyInsideforLiveTV;
    const relatedProviders = getRelatedProviders(channel, providers);
    const relatedProvider = relatedProviders.find((x) => x.type === 'thirdParty');

    return {
      name: channel.name,
      id: channel.contentId, // ~ `channelId`
      type: 'program',
      isSubscribed: isSubscribed,
      image: getChannelLogoUrl(channel),
      channelNumber: Number(channel.chanNo),
      supportsLiveTV: supportsLiveTV,
      isLimitedToHomeNetwork: isLimitedToHomeNetwork,
      relatedProvider,
    };
  });
}

function getRelatedProviders(channel: Channel, providers: Provider[]): (ThirdPartyProvider | SvodProvider)[] {
  return providers.filter((provider): provider is ThirdPartyProvider | SvodProvider => {
    const isValidType = provider.type === 'thirdParty' || provider.type === 'svod';
    return (
      isValidType &&
      'params' in provider &&
      'channelIds' in provider.params &&
      provider.params.channelIds !== undefined &&
      provider.params.channelIds.split(',').includes(channel.contentId)
    );
  });
}
